<template>
  <div class="job-architecture-page">
    <TopBarForDrawer>
      <template #breadcrumbs>
        <router-link
          :to="{ name: 'job-architecture', query: { view: 'list' } }"
          class="breadcrumb-back-link text-body-2"
        >
          Job Architecture
        </router-link>
        <span class="breadcrumb-separator text-body-2">/</span>
        <span class="text-body-2 text-medium-emphasis">Compare profiles</span>
      </template>
      <template #page-title>
        <TopBarForDrawerTitle
          :page-title="route.meta.title"
          :page-description="pageDescription"
        />
      </template>
      <template #summaries>
        <h3
          v-if="profiles.length > 0"
          class="text-body-2 text-textDarkest text-right"
        >
          Job Profiles
          <span class="text-textBrand">{{ profiles.length }}</span>
        </h3>
      </template>
    </TopBarForDrawer>

    <div class="job-architecture-controls-row">
      <div class="job-architecture-filters-left">
        <RowsFilterDropdown
          v-if="tableControls"
          :fields="tableControls.orderedFieldsForRows || tableControls.allFields"
          :visible-ids="tableControls.visibleFieldIdsArray"
          @toggle-field="tableControls.toggleFieldVisibility"
        />
        <AddJobProfileSearch
          :existing-ulids="existingCompareUlids"
          @add-profile="handleAddProfile"
        />
      </div>
      <div class="job-architecture-filters-right">
        <v-btn
          variant="outlined"
          rounded="lg"
          class="filter-style-btn"
          :disabled="!canDownloadCsv"
          @click="handleDownloadCsv"
        >
          <v-icon icon="mdi-download" start />
          Download CSV
        </v-btn>
      </div>
    </div>

    <template v-if="noSelection">
      <p class="text-medium-emphasis pa-4">
        No job profiles selected. Select job profiles in the
        <router-link :to="{ name: 'job-architecture', query: { view: 'list' } }" class="breadcrumb-back-link">
          list view
        </router-link>
        and click “Compare selected”.
      </p>
    </template>

    <template v-else>
      <JobProfileComparisonTable
        v-if="fieldSettingsLoaded && profiles.length > 0"
        :profiles="profiles"
        :field-keys-order="fieldKeysOrder"
        :hidden-field-keys="hiddenFieldKeys"
        @update:controls="tableControls = $event"
        @remove-profile="handleRemoveProfile"
      />
      <p
        v-else-if="profilesLoading"
        class="text-medium-emphasis pa-4"
      >
        Loading profiles…
      </p>
      <p
        v-else-if="profilesError"
        class="text-medium-emphasis pa-4"
      >
        Failed to load profiles. Please try again.
      </p>
      <p
        v-else
        class="text-medium-emphasis pa-4"
      >
        No profile data could be loaded for the selected profiles.
      </p>
    </template>
  </div>
</template>

<script setup>
import { format } from 'date-fns';
import {
  ref, computed, watch, onMounted,
} from 'vue';
import { useRoute, useRouter } from 'vue-router';
import { storeToRefs } from 'pinia';
import { getProfilesByUlids } from '@/api/architecture';
import { useAppLoading } from '@/stores/app/loading';
import { useAppSnackbar } from '@/stores/app/snackbar';
import { useJobProfileComparisonFieldSettings } from '@/composables/useJobProfileComparisonFieldSettings';
import { normalizeCustomFields } from '@/stores/jobArchitecture/helpers';
import { useJobArchitecture } from '@/stores/jobArchitecture/jobArchitecture';
import { useJobProfiles } from '@/stores/jobProfiles/jobProfiles';
import { pageDescriptions } from '@/helpers/constants/pageDescriptions';
import { useTracking } from '@/stores/tracking/tracking';
import TopBarForDrawer from '@/components/shared/topbar/TopBarForDrawer.vue';
import TopBarForDrawerTitle from '@/components/shared/topbar/TopBarForDrawerTitle.vue';
import JobProfileComparisonTable from '@/components/jobArchitecture/JobProfileComparisonTable.vue';
import AddJobProfileSearch from '@/components/jobArchitecture/AddJobProfileSearch.vue';
import RowsFilterDropdown from '@/components/jobArchitecture/RowsFilterDropdown.vue';

const route = useRoute();
const router = useRouter();
const jobArchitectureStore = useJobArchitecture();
const { setCompareSelectionUlids, loadTree } = jobArchitectureStore;
const { treeData } = storeToRefs(jobArchitectureStore);

const { setLoading } = useAppLoading();
const { listLoading: profilesLoading } = storeToRefs(useAppLoading());

const { selectedProfiles } = storeToRefs(useJobProfiles());

const tableControls = ref(null);
const profiles = ref([]);
const profilesError = ref(false);
const { fieldKeysOrder, hiddenFieldKeys, fieldSettingsLoaded } = useJobProfileComparisonFieldSettings();

const profileUlids = computed(() => {
  const raw = route.query?.profiles || '';

  return raw.split(',').map((s) => s.trim()).filter(Boolean);
});

const noSelection = computed(() => profileUlids.value.length === 0);

const existingCompareUlids = computed(() => profiles.value.map((profile) => profile.ulid));

const pageDescription = computed(() => pageDescriptions()[route.name] || '');

const csvDownloadFileName = computed(() => `job-profiles-compare-${format(new Date(), 'MM-dd-yyyy')}.csv`);

const canDownloadCsv = computed(() => (
  !profilesLoading.value
  && profiles.value.length > 0
  && tableControls.value?.hasExportableResults === true
));

const handleDownloadCsv = () => {
  useTracking().jaMatrixDownloadedCsv();

  tableControls.value?.downloadCsv?.({ fileName: csvDownloadFileName.value });
};

const loadProfiles = async () => {
  const ulids = profileUlids.value;

  if (ulids.length === 0) {
    profiles.value = [];
    profilesError.value = false;
    tableControls.value = null;

    setCompareSelectionUlids([]);

    return;
  }

  setLoading({ listLoading: true });

  profilesError.value = false;

  try {
    const data = await getProfilesByUlids(ulids);

    profiles.value = (data || []).map((profile) => {
      const customFields = normalizeCustomFields(profile.custom_fields);

      return { ...profile, custom_fields: customFields };
    });

    if (!profiles.value.length) {
      tableControls.value = null;
    }

    setCompareSelectionUlids(profiles.value.map((profile) => profile.ulid));
  } catch (err) {
    useAppSnackbar().addErrorSnackbar({
      text: `Job profiles load error: ${err.message || err}`,
    });

    profiles.value = [];
    profilesError.value = true;
    tableControls.value = null;
    setCompareSelectionUlids([]);
  } finally {
    setLoading({ listLoading: false });
  }
};

const removeProfilesFromLibraryStore = (ulid) => {
  if (!selectedProfiles.value.has(ulid)) return;
  selectedProfiles.value.delete(ulid);
};

const handleRemoveProfile = (ulid) => {
  profiles.value = profiles.value.filter((profile) => profile.ulid !== ulid);

  setCompareSelectionUlids(profiles.value.map((profile) => profile.ulid));

  removeProfilesFromLibraryStore(ulid);

  router.replace({
    query: {
      ...route.query,
      profiles: profiles.value.map((profile) => profile.ulid).join(','),
    },
  });
};

const addProfilesToHrisLibraryStore = (ulid) => {
  if (selectedProfiles.value.has(ulid)) return;
  selectedProfiles.value.add(ulid);
};

const handleAddProfile = async (ulid) => {
  if (profiles.value.some((profile) => profile.ulid === ulid)) return;

  useTracking().jaMatrixAddedJobProfile();

  addProfilesToHrisLibraryStore(ulid);

  setLoading({ listLoading: true });

  try {
    const data = await getProfilesByUlids([ulid]);
    const newProfile = data?.[0];

    if (!newProfile) return;

    const customFields = normalizeCustomFields(newProfile.custom_fields);

    profiles.value = [{ ...newProfile, custom_fields: customFields }, ...profiles.value];

    setCompareSelectionUlids(profiles.value.map((profile) => profile.ulid));

    router.replace({
      query: {
        ...route.query,
        profiles: profiles.value.map((profile) => profile.ulid).join(','),
      },
    });
  } catch (err) {
    useAppSnackbar().addErrorSnackbar({
      text: `Could not add profile: ${err.message || err}`,
    });
  } finally {
    setLoading({ listLoading: false });
  }
};

watch(
  () => route.query.profiles,
  () => {
    loadProfiles();
  },
  { immediate: true },
);

onMounted(async () => {
  if (!treeData.value) {
    await loadTree();
  }
});
</script>

<style scoped lang="less">
@import '@/less/mixins';

.job-architecture-page {
  display: flex;
  flex-direction: column;
  width: 100%;
  max-width: 1400px;
  margin-left: auto;
  margin-right: auto;
  padding: 0 24px 16px;
}

:deep(.topbar) {
  padding-left: 0;
  padding-right: 0;
}

:deep(.topbar-breadcrumbs) {
  display: flex;
  align-items: center;
  gap: 8px;
}

.job-architecture-controls-row {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 24px;
  margin-bottom: 12px;
  flex-wrap: wrap;
}

.breadcrumb-back-link {
  color: @text-brand;
  text-decoration: none;
}

.breadcrumb-back-link:hover {
  text-decoration: underline;
}

.breadcrumb-separator {
  color: @grey-500;
}

.job-architecture-filters-left,
.job-architecture-filters-right {
  display: flex;
  gap: 12px;
  flex-wrap: wrap;
  align-items: center;
}

.filter-style-btn {
  height: 40px !important;
  min-height: 40px !important;
  background-color: @bg-default !important;
  border: 1px solid @stroke-default;
  color: @text-darkest;
  font-weight: 400;
  text-transform: none;
}
</style>
